"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useCollection = void 0;
const notistack_1 = require("notistack");
const react_intl_1 = require("react-intl");
const react_1 = require("react");
const react_router_1 = require("react-router");
const qs_1 = require("qs");
const constants_1 = require("@framework/constants");
const provider_api_1 = require("@framework/provider-api");
const use_api_call_1 = require("./use-api-call");
const use_deep_compare_effect_1 = require("./use-deep-compare-effect");
const utils_1 = require("./utils");
const POLICY_ERROR_KEYS = [
    "used_into_previous_password",
    "password_min_length",
    "diff_case_letters",
    "numbers_in_password",
    "special_characters",
    "max_ident_chars_in_a_row",
    "dict_word_password",
    "cyrillic_symbols",
];
const applyPasswordPolicyApiErrors = (form, e, passwordField) => {
    if (e.interlayerErrors?.length) {
        e.interlayerErrors.forEach(({ key }) => {
            if (!key) {
                return;
            }
            const field = POLICY_ERROR_KEYS.includes(key) || key.toLowerCase().includes("password") ? passwordField : key;
            form.setError(field, { type: "custom", message: `form.validations.${key}` });
        });
        return;
    }
    const errors = e.getLocalizedValidationErrors();
    Object.keys(errors).forEach(key => {
        form.setError(key, { type: "custom", message: errors[key] });
    });
};
const defaultFilter = ({ id: _id, ...rest }) => rest;
const defaultRedirect = (baseUrl, search, id) => (id ? `${baseUrl}/${id}` : `${baseUrl}?${(0, qs_1.stringify)(search)}`);
const useCollection = (options) => {
    const { empty = {}, search: data = {}, baseUrl, embedded, filter = defaultFilter, redirect = defaultRedirect, } = options;
    const { id } = embedded ? { id: "" } : (0, react_router_1.useParams)();
    const location = (0, react_router_1.useLocation)();
    const navigate = (0, react_router_1.useNavigate)();
    const { formatMessage } = (0, react_intl_1.useIntl)();
    const [isLoading, setIsLoading] = (0, react_1.useState)(false);
    const [isCreateUserDialogOpen, setIsCreateUserDialogOpen] = (0, react_1.useState)(false);
    const [isChangePasswordDialogOpen, setIsChangePasswordDialogOpen] = (0, react_1.useState)(false);
    const [isChangeStatusDialogOpen, setIsChangeStatusDialogOpen] = (0, react_1.useState)(false);
    const [rows, setRows] = (0, react_1.useState)([]);
    const [count, setCount] = (0, react_1.useState)(0);
    const [selected, setSelected] = (0, react_1.useState)(empty);
    const getSearchParams = (data) => {
        const search = (0, qs_1.parse)(location.search.substring(1), { decoder: utils_1.decoder });
        return Object.entries(data).reduce((memo, [key, value]) => Object.assign(memo, { [key]: search[key] ?? value }), {});
    };
    const [search, setSearch] = (0, react_1.useState)(getSearchParams({
        skip: 0,
        take: constants_1.defaultItemsPerPage,
        ...data,
    }));
    const updateQS = (id) => {
        const { skip: _skip, take: _take, order: _order, ...rest } = search;
        const sameSearch = !id && location.search && (0, utils_1.deepEqual)(rest, getSearchParams(data));
        if (embedded || sameSearch) {
            return;
        }
        const shouldReplace = !location.search || location.search === "?" || !id;
        navigate(redirect(baseUrl, rest, id), {
            replace: shouldReplace,
        });
    };
    const { fn: fetchByQueryFn } = (0, use_api_call_1.useApiCall)(api => {
        const { skip, take } = search;
        const newSearch = {
            pageNumber: skip / take + 1,
            pageSize: take,
        };
        return api.fetchJson({
            url: baseUrl,
            data: newSearch,
            headers: {
                "Cache-Control": "no-cache",
            },
        });
    }, { success: false, error: false });
    const fetchByQuery = async () => {
        return fetchByQueryFn().then(json => {
            const paginated = json;
            setRows(paginated.items);
            setCount(paginated.totalCount);
            updateQS();
        });
    };
    const { fn: fetchByIdFn } = (0, use_api_call_1.useApiCall)((api, id) => {
        return api.fetchJson({
            url: `${baseUrl}/${id}`,
        });
    }, { success: false, error: false });
    const fetchById = async (id) => {
        return fetchByIdFn(undefined, id).then(json => {
            const row = json;
            setRows([row]);
            setCount(1);
            setSelected(row);
            setIsCreateUserDialogOpen(true);
        });
    };
    const fetch = async (id) => {
        setIsLoading(true);
        return (id ? fetchById(id) : fetchByQuery())
            .catch((e) => {
            if (e.status) {
                const errors = e.getLocalizedValidationErrors();
                Object.keys(errors).forEach((key) => {
                    const label = formatMessage({ id: `form.labels.${key.split(".").pop()}` });
                    const message = formatMessage({ id: errors[key] }, { label });
                    (0, notistack_1.enqueueSnackbar)(message, { variant: "error" });
                });
            }
            else {
                console.error(e);
                (0, notistack_1.enqueueSnackbar)(formatMessage({ id: "snackbar.error" }), { variant: "error" });
            }
        })
            .finally(() => {
            setIsLoading(false);
        });
    };
    const openUserCreateDialog = () => {
        setSelected(empty);
        setIsCreateUserDialogOpen(true);
    };
    const handleCreateUserCancel = () => {
        setIsCreateUserDialogOpen(false);
        updateQS();
    };
    const { fn: handleCreateUserConfirmFn } = (0, use_api_call_1.useApiCall)(async (api, values) => {
        const { id, password, login } = values;
        let data = filter(values);
        if (data.userGroup !== undefined) {
            data.userGroup = Number(data.userGroup);
        }
        if (data.userGroup === 0) {
            throw new Error("Cannot create web user with userGroup ADMINISTRATOR");
        }
        if (password && login) {
            const key = (await api.fetchJson({
                url: "/auth/genpk",
                method: "POST",
                data: { usr_key: String(login).trim().toLowerCase() },
            }));
            const encryptedPassword = await (0, provider_api_1.encryptLoginPasswordRsaOaep)(key.pk, password);
            data = { ...data, password: encryptedPassword };
        }
        return api.fetchJson({
            url: id ? `${baseUrl}/${id}` : baseUrl,
            method: id ? "PUT" : "POST",
            data,
        });
    }, { success: false, error: false });
    const handleCreateUserConfirm = async (values, form) => {
        if (values.email === "") {
            values.email = null;
        }
        return handleCreateUserConfirmFn(form, values)
            .then(() => {
            (0, notistack_1.enqueueSnackbar)(formatMessage({ id: id || values.id ? "snackbar.updated" : "snackbar.created" }), {
                variant: "success",
            });
            setIsCreateUserDialogOpen(false);
            form.reset(values);
            return fetch();
        })
            .catch((e) => {
            if (e.status === 400) {
                applyPasswordPolicyApiErrors(form, e, "password");
            }
            else if (e.status) {
                (0, notistack_1.enqueueSnackbar)(formatMessage({ id: `snackbar.${e.message}` }), { variant: "error" });
            }
            else {
                console.error(e);
                (0, notistack_1.enqueueSnackbar)(formatMessage({ id: "snackbar.error" }), { variant: "error" });
            }
        });
    };
    const openChangePasswordDialog = (item) => {
        return () => {
            setSelected(item);
            setIsChangePasswordDialogOpen(true);
        };
    };
    const handleChangePasswordCancel = () => {
        setIsChangePasswordDialogOpen(false);
    };
    const { fn: handleChangePasswordConfirmFn } = (0, use_api_call_1.useApiCall)(async (api, values) => {
        const { id, newPassword, login } = values;
        const key = (await api.fetchJson({
            url: "/auth/genpk",
            method: "POST",
            data: { usr_key: login.trim().toLowerCase() },
        }));
        const encryptedPassword = await (0, provider_api_1.encryptLoginPasswordRsaOaep)(key.pk, newPassword);
        return api.fetchJson({
            url: `${baseUrl}/${id}`,
            method: "PUT",
            data: { password: encryptedPassword },
        });
    }, { success: false, error: false });
    const handleChangePasswordConfirm = async (values, form) => {
        const { id, newPassword, login } = values;
        if (id === undefined || newPassword === undefined || login === undefined) {
            return;
        }
        return handleChangePasswordConfirmFn(form, { id, newPassword, login })
            .then(() => {
            (0, notistack_1.enqueueSnackbar)(formatMessage({ id: "snackbar.passwordChanged" }), { variant: "success" });
            setIsChangePasswordDialogOpen(false);
            return fetch();
        })
            .catch((e) => {
            if (e.status === 400) {
                applyPasswordPolicyApiErrors(form, e, "newPassword");
            }
            else if (e.status) {
                (0, notistack_1.enqueueSnackbar)(formatMessage({ id: `snackbar.${e.message}` }), { variant: "error" });
            }
            else {
                console.error(e);
                (0, notistack_1.enqueueSnackbar)(formatMessage({ id: "snackbar.error" }), { variant: "error" });
            }
        });
    };
    const openChangeStatusDialog = (item) => {
        return () => {
            setSelected(item);
            setIsChangeStatusDialogOpen(true);
        };
    };
    const handleChangeStatusCancel = () => {
        setIsChangeStatusDialogOpen(false);
    };
    const { fn: handleChangeStatusConfirmFn } = (0, use_api_call_1.useApiCall)((api, values) => {
        const { id, isActive } = values;
        return api.fetchJson({
            url: `${baseUrl}/${id}`,
            method: "PUT",
            data: { isActive },
        });
    }, { success: false, error: false });
    const handleChangeStatusConfirm = async (values) => {
        return handleChangeStatusConfirmFn(undefined, values)
            .then(() => {
            const messageId = values.isActive ? "snackbar.activated" : "snackbar.deactivated";
            (0, notistack_1.enqueueSnackbar)(formatMessage({ id: messageId }), { variant: "success" });
            setIsChangeStatusDialogOpen(false);
            return fetch();
        })
            .catch((e) => {
            if (e.status) {
                (0, notistack_1.enqueueSnackbar)(formatMessage({ id: `snackbar.${e.message}` }), { variant: "error" });
            }
            else {
                console.error(e);
                (0, notistack_1.enqueueSnackbar)(formatMessage({ id: "snackbar.error" }), { variant: "error" });
            }
        });
    };
    const handleChangePage = (_e, page) => {
        setSearch({
            ...search,
            skip: (page - 1) * search.take,
        });
    };
    (0, use_deep_compare_effect_1.useDeepCompareEffect)(() => {
        if (!id) {
            setIsCreateUserDialogOpen(false);
        }
        if ((0, utils_1.hasAwaited)(search)) {
            return;
        }
        void fetch(id);
    }, [search, id]);
    (0, use_deep_compare_effect_1.useDeepCompareEffect)(() => {
        setSearch(getSearchParams(search));
    }, [location]);
    return {
        rows,
        count,
        search,
        selected,
        isLoading,
        isCreateUserDialogOpen,
        isChangePasswordDialogOpen,
        isChangeStatusDialogOpen,
        openUserCreateDialog,
        handleCreateUserCancel,
        handleCreateUserConfirm,
        openChangePasswordDialog,
        handleChangePasswordCancel,
        handleChangePasswordConfirm,
        openChangeStatusDialog,
        handleChangeStatusCancel,
        handleChangeStatusConfirm,
        handleChangePage,
    };
};
exports.useCollection = useCollection;
//# sourceMappingURL=use-collection.js.map